import type { HelpContent } from '../types'

/**
 * Help panel + first-run walkthrough copy for the Find Leads (lead discovery)
 * page. Keep every string em-dash free. The core mental model to teach: search
 * and import are free, only enriching a preview costs a credit.
 */
export const LEAD_DISCOVERY_HELP: HelpContent = {
  key: 'lead-discovery',
  title: 'Find Leads',
  intro:
    'Find Leads searches LinkedIn and a data provider for new prospects that match your ideal customer profile, then lets you add them to your pipeline.',
  panel: [
    {
      heading: 'How it works',
      items: [
        {
          text: 'Set filters on the left, then click Search Leads. Pressing Enter locks in a filter, it does not start the search.',
        },
        {
          text: 'Results come back as a mix of your own network (already complete) and provider previews (partly hidden).',
        },
        {
          text: 'A preview must be enriched before it becomes a usable lead. Enriching costs one credit and reveals the LinkedIn URL, full name, location, and photo.',
        },
        {
          text: 'Enriched leads are saved to your Leads page automatically. You never lose what you paid for, even if you leave this page mid-batch.',
        },
        {
          text: 'The Import button is for leads from your own network results, which are free to add. A single import researches the lead automatically.',
        },
        {
          text: 'Leads already in your database show an Added badge and are never charged again, even if you re-enrich them.',
        },
      ],
    },
    {
      heading: 'The filters',
      items: [
        {
          term: 'Job Title',
          text: 'Roles to target. Add several and press Enter after each; results match any of them.',
        },
        { term: 'Company', text: 'Narrow to people at one company by name.' },
        { term: 'Industry', text: 'Click pills to include industries. Click a pill again to remove it.' },
        {
          term: 'Location',
          text: 'Country, US state, or metro. Pick from the list as you type, or enter your own.',
        },
        { term: 'Keywords', text: 'Extra terms to match in a profile, like saas or fintech.' },
      ],
    },
    {
      heading: 'Credits',
      items: [
        {
          text: 'Credits are prepaid and pay for enriching previews, one credit each. Searching and importing are free.',
        },
        {
          text: 'Your balance refills monthly on the reset date. You can top up 100 credits for five dollars at any time.',
        },
        {
          text: 'There is also a daily limit on enrichment batches (20 per day on Pro, each up to 25 leads). If you hit it, everything already enriched is safe in your Leads page, and the limit resets at midnight UTC.',
        },
      ],
    },
    {
      heading: 'Reading results',
      items: [
        {
          term: 'Preview',
          text: 'A partial result with contact details hidden. Enrich it to unlock them, then Import.',
        },
        { term: 'Cards or Table', text: 'Two layouts for the same results. Your choice is remembered.' },
        {
          term: 'Select all',
          text: 'Selects every importable lead on the current page only, not across other pages.',
        },
        {
          term: 'Next',
          text: 'Moves through results and loads more from the provider when the current set runs out.',
        },
      ],
    },
    {
      heading: 'Good to know',
      items: [
        {
          text: 'Filters may arrive pre-filled from your saved ideal customer profile in Settings.',
        },
        {
          text: 'Recent searches are saved on this device only, so they do not follow you to another browser.',
        },
      ],
    },
  ],
  tour: [
    {
      id: 'welcome',
      title: 'Find new leads',
      body: 'Search LinkedIn and a data provider for people who match your ideal customer profile, then add them to your pipeline.',
    },
    {
      id: 'filters',
      target: 'discover-filters',
      title: 'Set your filters',
      body: 'Add a job title, company, industry, location, or keywords. Pressing Enter locks a filter in place, and the Search Leads button runs the search.',
    },
    {
      id: 'credits',
      target: 'discover-credits',
      title: 'How credits work',
      body: 'Credits pay for enriching previews, one credit each. Searching and importing are free. Your balance refills monthly.',
    },
    {
      id: 'results',
      target: 'discover-results',
      title: 'Preview and enrich',
      body: 'Provider results start as Previews with details hidden. Enrich one for a credit to unlock it. Enriched leads are saved to your Leads page automatically, so they are yours even if you leave this page.',
    },
    {
      id: 'views',
      target: 'discover-view-toggle',
      title: 'Cards or table',
      body: 'View results as cards or a compact table, and use Select all to act on a whole page at once. Select all covers the current page only.',
    },
    {
      id: 'done',
      title: 'You are set',
      body: 'Imported leads land in your pipeline under Leads. Reopen this walkthrough anytime from the Help button.',
    },
  ],
}
